import { Injectable, signal, computed } from '@angular/core';
import { BreadcrumbItem, BreadcrumbLevel, UserRole } from './breadcrumb.interface';

@Injectable({
  providedIn: 'root'
})
export class BreadcrumbService {
  private readonly STORAGE_KEY = 'cubus_breadcrumb_data';
  
  // Private signal for breadcrumb items
  private breadcrumbItems = signal<BreadcrumbItem[]>([]);
  // Private signal for icon path
  private iconPathSignal = signal<string>(this.getStoredIconPath());

  // Public readonly computed for external access
  readonly items = computed(() => this.breadcrumbItems());
  readonly iconPath = computed(() => this.iconPathSignal());

  /**
   * Set breadcrumb icon path directly
   */
  setIconPath(iconPath: string): void {
    const data = this.getBreadcrumbData();
    data.iconPath = iconPath;
    this.saveBreadcrumbData(data);
    // Update the signal to trigger reactivity
    this.iconPathSignal.set(iconPath);
  }

  /**
   * Get stored icon path from localStorage
   */
  private getStoredIconPath(): string {
    const data = this.getBreadcrumbData();
    return data.iconPath ?? 'assets/svgs/project-icon.svg'; // default to project icon
  }



  /**
   * Get current icon path
   */
  getIconPath(): string {
    return this.iconPathSignal();
  }

  /**
   * Set customer name in localStorage and update breadcrumbs
   */
  setCustomer(customerId: string | number, customerName: string): void {
    const data = this.getBreadcrumbData();
    data.customerId = customerId;
    data.customerName = customerName;
    this.saveBreadcrumbData(data);
    this.updateBreadcrumbs();
  }

  /**
   * Set project name in localStorage and update breadcrumbs
   */
  setProject(projectId: string | number, projectName: string): void {
    const data = this.getBreadcrumbData();
    data.projectId = projectId;
    data.projectName = projectName;
    this.saveBreadcrumbData(data);
    this.updateBreadcrumbs();
  }

  /**
   * Set repository name in localStorage and update breadcrumbs
   */
  setRepository(repositoryId: string | number, repositoryName: string): void {
    const data = this.getBreadcrumbData();
    data.repositoryId = repositoryId;
    data.repositoryName = repositoryName;
    this.saveBreadcrumbData(data);
    this.updateBreadcrumbs();
  }

  /**
   * Build breadcrumbs based on current route and user role
   */
  buildBreadcrumbs(currentLevel: 'customers' | 'projects' | 'repositories' | 'documents' | 'files', userRole: UserRole = 'customer'): void {
    const data = this.getBreadcrumbData();
    const items: BreadcrumbItem[] = [];

    // Admin flow: Customers > Projects > Repositories > Files
    if (userRole === 'admin') {
      // Always show Customers for admin
      items.push({
        label: 'Customers',
        route: currentLevel === 'customers' ? undefined : '/admin/customers',
        isActive: currentLevel === 'customers'
      });

      // Show Projects if we're at projects level or deeper
      if (currentLevel !== 'customers') {
        const projectLabel = data.customerName ?? 'Projects';
        items.push({
          label: projectLabel,
          route: currentLevel === 'projects' ? undefined : `/admin/customers/${data.customerId}/projects`,
          isActive: currentLevel === 'projects'
        });
      }

      // Show Project Details if we're at repositories, documents, or files level
      if (currentLevel === 'repositories' || currentLevel === 'documents' || currentLevel === 'files') {
        const projectLabel = data.projectName ?? 'Project';
        items.push({
          label: projectLabel,
          route: (currentLevel === 'repositories' || currentLevel === 'documents') ? undefined : `/admin/customers/${data.customerId}/projects/${data.projectId}`,
          isActive: currentLevel === 'repositories' || currentLevel === 'documents'
        });
      }

      // Show Files if we're at files level
      if (currentLevel === 'files') {
        const fileLabel = data.repositoryName ?? 'Files';
        items.push({
          label: fileLabel,
          route: undefined,
          isActive: true
        });
      }
    }
    // Customer flow: Projects > Repositories > Files
    else {
      // Always show Projects for customer
      items.push({
        label: 'Projects',
        route: currentLevel === 'projects' ? undefined : '/customer/projects',
        isActive: currentLevel === 'projects'
      });

      // Show Project Details if we're at repositories, documents, or files level  
      if (currentLevel === 'repositories' || currentLevel === 'documents' || currentLevel === 'files') {
        const projectLabel = data.projectName ?? 'Project';
        items.push({
          label: projectLabel,
          route: (currentLevel === 'repositories' || currentLevel === 'documents') ? undefined : `/customer/projects/${data.projectId}`,
          isActive: currentLevel === 'repositories' || currentLevel === 'documents'
        });
      }

      // Show Files if we're at files level
      if (currentLevel === 'files') {
        const fileLabel = data.repositoryName ?? 'Files';
        items.push({
          label: fileLabel,
          route: undefined,
          isActive: true
        });
      }
    }

    this.breadcrumbItems.set(items);
  }

  /**
   * Clear all breadcrumb data (useful for logout)
   */
  clear(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this.breadcrumbItems.set([]);
    // Reset icon path to default
    this.iconPathSignal.set('assets/svgs/project-icon.svg');
  }

  /**
   * Get stored customer name
   */
  getStoredCustomerName(): string | undefined {
    return this.getBreadcrumbData().customerName;
  }

  /**
   * Get stored project name
   */
  getStoredProjectName(): string | undefined {
    return this.getBreadcrumbData().projectName;
  }

  /**
   * Get stored repository name
   */
  getStoredRepositoryName(): string | undefined {
    return this.getBreadcrumbData().repositoryName;
  }

  /**
   * Get breadcrumb data from localStorage
   */
  private getBreadcrumbData(): BreadcrumbLevel {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  }

  /**
   * Save breadcrumb data to localStorage
   */
  private saveBreadcrumbData(data: BreadcrumbLevel): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
  }

  /**
   * Update breadcrumbs based on current stored data
   */
  private updateBreadcrumbs(): void {
    // This method can be called to refresh breadcrumbs after data changes
    // The actual level should be determined by the current component
  }
}